import { lazy, type ComponentType } from 'react';
import type { ModuleTemplate } from '../types/module';
import type { LearnModuleId } from '../learn';
import {
  SCALE_KINDS,
  type ScaleKind,
} from '../../pages/tools/_shared/scaleKinds';
import { getAvailableModules, type TriadQuality } from './registry';

const ScaleShapesPage = lazy(
  () => import('../../pages/tools/_shared/ScaleShapesPage')
);
const TriadShapesPage = lazy(
  () => import('../../pages/tools/_shared/TriadShapesPage')
);

/** Hand-built pages; everything else is derived from the registry entry. */
const DEDICATED_PAGES: Record<string, ComponentType> = {
  'guided-solo': lazy(() => import('../../pages/tools/guided-solo')),
  'tab-practice': lazy(() => import('../../pages/tools/tab-practice')),
  'guided-chord-progression': lazy(
    () => import('../../pages/tools/guided-chord-progression')
  ),
};

const SCALE_MODULES = Object.fromEntries(
  (Object.keys(SCALE_KINDS) as ScaleKind[]).map((kind) => [
    SCALE_KINDS[kind].moduleId,
    kind,
  ])
) as Record<string, ScaleKind>;

/** Old per-position scale URLs → the all-positions module, starting there. */
export const LEGACY_SCALE_REDIRECTS = (
  Object.keys(SCALE_KINDS) as ScaleKind[]
).flatMap((kind) =>
  Array.from({ length: SCALE_KINDS[kind].totalPositions }, (_, i) => ({
    from: `/learn/${kind}-position-${i + 1}`,
    to: `/learn/${SCALE_KINDS[kind].moduleId}?position=${i + 1}`,
  }))
);

function named(Page: ComponentType, moduleId: string): ComponentType {
  Page.displayName = `ModulePage(${moduleId})`;
  return Page;
}

function pageFor(template: ModuleTemplate): ComponentType | undefined {
  const moduleId = template.id as LearnModuleId;
  const dedicated = DEDICATED_PAGES[template.id];
  if (dedicated) return dedicated;

  const scaleKind = SCALE_MODULES[template.id];
  if (scaleKind) {
    return named(() => <ScaleShapesPage scaleKind={scaleKind} />, moduleId);
  }

  const { triadGroupIndex, chordQuality } = template.defaults;
  if (template.learnFamily === 'triads') {
    return named(
      () => (
        <TriadShapesPage
          moduleId={moduleId}
          groupIndex={triadGroupIndex ?? 0}
          triadQuality={(chordQuality ?? 'major') as TriadQuality}
        />
      ),
      moduleId
    );
  }

  return undefined;
}

export const MODULE_ROUTES = getAvailableModules().flatMap((template) => {
  const Component = pageFor(template);
  return Component ? [{ ...template, Component }] : [];
});

/** Route → page lookup (catalog metadata and access live in the registry). */
export const MODULE_PAGES: Record<string, ComponentType> = Object.fromEntries(
  MODULE_ROUTES.map(({ configRoute, Component }) => [configRoute, Component])
);
